import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Download, Trash2, ExternalLink, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const scoreColor = (s) => (s >= 85 ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : s >= 70 ? 'text-amber-400 border-amber-500/40 bg-amber-500/10' : 'text-orange-400 border-orange-500/40 bg-orange-500/10');

const AdminInspections = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [items, setItems] = useState([]);
  const [url, setUrl] = useState('');
  const [city, setCity] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/recent-inspections`, authHeaders);
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load inspections');
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);

  const importUrl = async () => {
    if (!url.trim()) { toast.error('Paste a Base44 report link first'); return; }
    setImporting(true);
    try {
      const res = await axios.post(`${API}/admin/recent-inspections/import`, { url: url.trim(), city: city.trim() || null }, authHeaders);
      toast.success(`Imported ${res.data.vehicle}`);
      setUrl('');
      setCity('');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed — check the link is public');
    } finally {
      setImporting(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Remove this inspection from the public showcase?')) return;
    try {
      await axios.delete(`${API}/admin/recent-inspections/${id}`, authHeaders);
      toast.success('Removed');
      fetchItems();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }} data-testid="admin-inspections">
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-5xl mx-auto pl-6 pr-20 sm:pr-32 py-4 flex justify-between items-center">
          <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/10" onClick={() => navigate('/admin')} data-testid="back-admin-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
          </Button>
          <a href="/recent-inspections" target="_blank" rel="noopener noreferrer" className="text-sm text-orange-300 hover:text-orange-200 inline-flex items-center gap-1.5" data-testid="view-public-page-link">
            View public page <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk' }}>
          <Camera className="w-8 h-8 text-orange-500" /> Recent Inspections
        </h1>
        <p className="text-zinc-400 mb-8">Paste a CarLens Base44 report link and the showcase card fills in automatically — vehicle, score, photos and summary.</p>

        {/* Import box */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6 mb-10">
          <label className="block text-xs text-zinc-500 uppercase tracking-wide mb-2">Base44 report link</label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://carlens-inspect.base44.app/inspectionreport?id=..."
            className="bg-zinc-950 border-zinc-700 text-white mb-4"
            data-testid="import-url-input"
          />
          <label className="block text-xs text-zinc-500 uppercase tracking-wide mb-2">City to display (optional)</label>
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g. Jacksonville, FL — leave blank to hide"
            className="bg-zinc-950 border-zinc-700 text-white mb-4 max-w-sm"
            data-testid="import-city-input"
          />
          <Button onClick={importUrl} disabled={importing} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="import-btn">
            <Download className="w-4 h-4 mr-2" /> {importing ? 'Importing…' : 'Import & Publish'}
          </Button>
        </div>

        {/* List */}
        <h2 className="text-2xl font-semibold text-white mb-4">Published ({items.length})</h2>
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-zinc-800 bg-white/[0.02] p-4 flex items-center gap-4" data-testid={`admin-inspection-${it.id}`}>
              <img src={(it.photos || [])[0]} alt={it.vehicle} className="w-24 h-16 object-cover rounded-lg shrink-0 bg-black" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-semibold">{it.vehicle}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${scoreColor(it.score)}`}>{it.score}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">{it.when}{it.city ? ` · ${it.city}` : ''} · {(it.photos || []).length} photos</p>
                <p className="text-xs text-zinc-500 truncate mt-1">{it.summary}</p>
              </div>
              <a href={it.reportUrl} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-orange-300 shrink-0" title="Open source report">
                <ExternalLink className="w-4 h-4" />
              </a>
              <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 shrink-0" onClick={() => removeItem(it.id)} data-testid={`delete-inspection-${it.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-center py-12 text-zinc-500" data-testid="admin-inspections-empty">No inspections published yet. Import one above.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInspections;
